import {
    Alert,
    AlertTitle,
    Divider,
    FormControl,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    type Dispatch,
    forwardRef,
    type SetStateAction,
    useImperativeHandle,
    useState,
} from 'react';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import LexoSwitch from '@/components/LexoSwitch';
import EmailTemplatePreview from '@/pages/QuoteIntake/EmailTemplatePreview';
import PostEmailTemplatePreview from '@/pages/QuoteIntake/PostEmailTemplatePreview';
import {
    createEmailTemplate,
    updateEmailTemplate,
    updatePostIntakeTemplate,
} from '@/services/EmailService';
import { errorSnackbar } from '@/utils/snackbar';
import type { IntakeDesignData, IntakeEmailData, IntakeEmailErrors, MatterTypeData } from './types';

interface Props {
    quoteTemplateId: number | null;
    setIsPostIntakeEnable(value: boolean): void;
    intakeEmail: IntakeEmailData;
    setIntakeEmail: Dispatch<SetStateAction<IntakeEmailData>>;
    matterType: MatterTypeData;
    intakeDesign: IntakeDesignData;
    lawyerSignature: string;
}

const IntakePostIntakeEmailSetup = forwardRef(function IntakePostIntakeEmailSetup(
    {
        quoteTemplateId,
        setIsPostIntakeEnable,
        intakeEmail,
        setIntakeEmail,
        matterType,
        intakeDesign,
        lawyerSignature,
    }: Props,
    ref,
) {
    const theme = useTheme();
    const [showTip, setShowTip] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [intakeEmailTemplateErrors, setIntakeEmailTemplateErrors] = useState<IntakeEmailErrors>();
    const [postIntakeSubjectError, setPostIntakeSubjectError] = useState<string>();

    const submit = async (): Promise<{ success: boolean }> => {
        // Quote template ID is required for creating or updating email templates
        if (!quoteTemplateId) return { success: false };

        // Validation
        const { subject, bodyBeforeLink, bodyAfterLink, postIntakeSubject } = intakeEmail;
        const isPostIntakeError =
            matterType.isPostIntakeEnable && (!postIntakeSubject || postIntakeSubject.length === 0);
        if (
            subject.length === 0 ||
            bodyBeforeLink.length === 0 ||
            bodyAfterLink.length === 0 ||
            isPostIntakeError
        ) {
            setIntakeEmailTemplateErrors({
                emailSubject: subject.length === 0 ? 'Subject is required' : undefined,
                emailBodyIntroduction:
                    bodyBeforeLink.length === 0 ? 'Email body introduction is required' : undefined,
                emailBodyClosing:
                    bodyAfterLink.length === 0 ? 'Email body closing is required' : undefined,
            });
            setPostIntakeSubjectError(isPostIntakeError ? 'Subject is required' : undefined);
            errorSnackbar('Please fill all required fields', true);
            return { success: false };
        }

        const payload = {
            emailTemplateType: 'IntakeEmailTemplate' as const,
            isQuoteApprovalEnable: intakeEmail.isQuoteApprovalOn,
            isQuoteReminderOn: false,
            emailSubject: intakeEmail.subject,
            emailBodyIntroduction: intakeEmail.bodyBeforeLink,
            emailBodyClosing: intakeEmail.bodyAfterLink,
            emailCC: matterType.emailCC?.length > 0 ? matterType.emailCC : null,
            emailReminder: null,
            emailSignature: lawyerSignature,
            expiry: intakeEmail.expiry,
        };
        try {
            if (intakeEmail.intakeEmailTemplateId) {
                if (isDirty) {
                    await updateEmailTemplate(
                        quoteTemplateId,
                        intakeEmail.intakeEmailTemplateId,
                        payload,
                    );
                }
            } else {
                const response = await createEmailTemplate(quoteTemplateId, payload);
                setIntakeEmail((prev) => ({
                    ...prev,
                    intakeEmailTemplateId: response.data.emailTemplateId,
                }));
            }
            await updatePostIntakeTemplate(quoteTemplateId, {
                isPostIntakeEnable: matterType.isPostIntakeEnable,
                emailSubject: intakeEmail.postIntakeSubject,
                intakeCompletionText: intakeDesign.textAfterSubmitIntakeForClient,
            });
            return { success: true };
        } catch (error: unknown) {
            console.error(error);
            errorSnackbar('Something went wrong!');
            return { success: false };
        }
    };

    useImperativeHandle(ref, () => ({ submit }));

    const handleStateChange = (event: { target: { value: string } }, key: string) => {
        setIsDirty(true);
        setIntakeEmailTemplateErrors(undefined);
        setIntakeEmail((prev) => ({
            ...prev,
            [key]: event.target.value,
        }));
    };

    return (
        <Stack
            padding={3}
            spacing={3}
            sx={{
                maxHeight: 'calc(100vh - 168px)',
                overflow: 'auto',
            }}
        >
            <Stack direction="row" spacing={3}>
                <Stack
                    spacing={3}
                    sx={{
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                        backgroundColor: 'neutral.white',
                        padding: '24px',
                        height: 'fit-content',
                        width: '100%',
                        margin: '32px 0px 32px 32px',
                    }}
                >
                    <Stack spacing={1}>
                        <Typography variant="h2">Intake email template</Typography>
                        <Typography variant="body2">
                            Customize your email template and see changes on preview on the right.
                        </Typography>
                        <FormControl fullWidth>
                            <TextField
                                value={intakeEmail.expiry || 180}
                                onChange={(ev) => handleStateChange(ev, 'expiry')}
                                fullWidth
                                placeholder="Quote Request Response"
                                id="intake-expiry-count"
                                type="text"
                                variant="outlined"
                                label="Number of days before intake expires"
                                name="intake-expiry-count"
                                disabled
                            />
                            {showTip && (
                                <Alert
                                    icon={<InfoSolid />}
                                    severity="info"
                                    onClose={() => setShowTip(false)}
                                    sx={{ mt: 2 }}
                                >
                                    <AlertTitle>Number of days</AlertTitle>
                                    This field is currently not editable
                                </Alert>
                            )}
                        </FormControl>
                    </Stack>
                    <Divider orientation="horizontal" />
                    <Stack spacing={4}>
                        <TextField
                            value={intakeEmail.subject}
                            onChange={(ev) => handleStateChange(ev, ev.target.name)}
                            fullWidth
                            placeholder="Intake Email Subject"
                            name={'subject'}
                            error={!!intakeEmailTemplateErrors?.emailSubject}
                            helperText={intakeEmailTemplateErrors?.emailSubject}
                            type="text"
                            variant="outlined"
                            label="Subject"
                        />
                        <TextField
                            value={intakeEmail.bodyBeforeLink}
                            onChange={(ev) => handleStateChange(ev, ev.target.name)}
                            name={'bodyBeforeLink'}
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={4}
                            error={!!intakeEmailTemplateErrors?.emailBodyIntroduction}
                            helperText={
                                intakeEmailTemplateErrors?.emailBodyIntroduction
                                    ? 'Email body introduction is required.'
                                    : ''
                            }
                            variant="outlined"
                            label="First part of body (before form link)"
                        />
                        <TextField
                            value={intakeEmail.bodyAfterLink}
                            onChange={(ev) => handleStateChange(ev, ev.target.name)}
                            name={'bodyAfterLink'}
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={4}
                            error={!!intakeEmailTemplateErrors?.emailBodyClosing}
                            helperText={
                                intakeEmailTemplateErrors?.emailBodyClosing
                                    ? 'Email body closing is required.'
                                    : ''
                            }
                            variant="outlined"
                            label="Second part of body (after form link)"
                        />
                    </Stack>
                </Stack>
                <EmailTemplatePreview
                    subject={intakeEmail?.subject}
                    bodyBeforeLink={intakeEmail?.bodyBeforeLink}
                    bodyAfterLink={intakeEmail?.bodyAfterLink}
                    signature={lawyerSignature}
                />
            </Stack>
            <Stack direction="row" spacing={3}>
                <Stack
                    spacing={3}
                    sx={{
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                        backgroundColor: 'neutral.white',
                        padding: '24px',
                        height: 'fit-content',
                        width: '100%',
                        margin: '10px 0px 32px 32px',
                    }}
                >
                    <Typography variant="h2">Post intake email template</Typography>
                    <Stack spacing={2}>
                        <Typography variant="body2">Send post-intake submission email.</Typography>
                        <LexoSwitch
                            checked={matterType.isPostIntakeEnable}
                            onChange={(event) => {
                                setPostIntakeSubjectError(undefined);
                                setIsPostIntakeEnable(event.target.checked);
                            }}
                        />
                    </Stack>
                    {matterType.isPostIntakeEnable && (
                        <TextField
                            value={intakeEmail.postIntakeSubject}
                            onChange={(ev) => handleStateChange(ev, ev.target.name)}
                            fullWidth
                            placeholder="Subject"
                            name={'postIntakeSubject'}
                            error={!!postIntakeSubjectError}
                            helperText={postIntakeSubjectError}
                            type="text"
                            variant="outlined"
                            label="Subject"
                        />
                    )}
                </Stack>
                <PostEmailTemplatePreview
                    isPostIntakeEnable={matterType.isPostIntakeEnable}
                    postIntakeSubject={intakeEmail.postIntakeSubject}
                    textAfterSubmitIntakeForClient={intakeDesign.textAfterSubmitIntakeForClient}
                    signature={lawyerSignature}
                />
            </Stack>
        </Stack>
    );
});

export default IntakePostIntakeEmailSetup;
